/**
 * 표 페이지 공용 "필터·정렬 초기화" 버튼.
 *
 * 디자인 — 시인성 강화 (이전 text-blue 연한 링크 → orange fill 버튼):
 *   - orange 50/600 배경, white 글자, shadow + 호버 강조
 *   - 빠르게 눈에 띄도록 일반 [검색] 버튼과 색상 분리
 *
 * 위치는 페이지별로 다르지만 시각 패턴은 동일.
 */

export interface ResetButtonProps {
  onClick: () => void;
  /** 추가 wrapper className */
  className?: string;
  /** 기본 라벨 변경 시 — 기본값 '↺ 필터·정렬 초기화' */
  label?: string;
  /** title 툴팁 */
  title?: string;
  disabled?: boolean;
}

export function ResetButton({
  onClick,
  className,
  label = '↺ 필터·정렬 초기화',
  title = '모든 검색·필터·정렬 조건을 초기 상태로',
  disabled,
}: ResetButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      disabled={disabled}
      className={`btn btn-warning-solid ${className ?? ''}`}
    >
      {label}
    </button>
  );
}

// ─── ResetMenu — 클릭 시 [기본 설정 / 모두 해제] 2가지 옵션 ───
// 영업진행: 기본 = 진행중 4단계 + 최근 3개년 (v2.203.0) / 모두 해제 = 전부 표시
// 매입목록: 기본 = 최근 2개년 + 단계·사업자·유형 전체 / 모두 해제 = 미입고 포함 전부
//
// 사용:
//   <ResetMenu
//     onResetDefault={() => ...}
//     onResetAll={() => ...}
//     defaultLabel="발행·판보 제외, 최근 2개년"
//   />

import { useEffect, useRef, useState } from 'react';

export interface ResetMenuProps {
  onResetDefault: () => void;
  onResetAll: () => void;
  /** "기본 설정으로" 선택 시 어떤 상태가 되는지 한 줄 설명 */
  defaultLabel: string;
}

export function ResetMenu({
  onResetDefault,
  onResetAll,
  defaultLabel,
}: ResetMenuProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') { e.preventDefault(); setOpen(false); } // 창까지 닫지 않게 (v2.231.0 · H-4)
    }
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        title="필터·정렬 초기화 옵션"
        className="btn btn-warning-solid"
      >
        ↺ 필터·정렬 초기화 <span className="text-xs">{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className="absolute top-full right-0 mt-1 bg-white border border-slate-300 rounded shadow-lg z-40 min-w-[260px] py-1 text-sm">
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              onResetDefault();
            }}
            className="w-full text-left px-3 py-2 hover:bg-blue-50 flex flex-col gap-0.5"
          >
            <span className="font-medium text-slate-800">📌 기본 설정으로</span>
            <span className="text-xs text-slate-500">{defaultLabel}</span>
          </button>
          <div className="border-t border-slate-100" />
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              onResetAll();
            }}
            className="w-full text-left px-3 py-2 hover:bg-blue-50 flex flex-col gap-0.5"
          >
            <span className="font-medium text-slate-800">🧹 모두 해제</span>
            <span className="text-xs text-slate-500">필터·정렬·검색 전부 비움 (전체 표시)</span>
          </button>
        </div>
      )}
    </div>
  );
}
